import { isSupabaseConfigured, supabase } from '../../lib/supabase'
import { activeTenant } from '../../lib/tenant'

const PUBLIC_KEY = 'webapp_public_site'

export type SiteContent = {
  seo: {
    siteName: string
    description: string
    canonicalUrl: string
    keywords: string
    robots: string
    ogTitle: string
    ogDescription: string
    ogImageUrl: string
    twitterCard: string
  }
  identity: {
    displayName: string
    legalName: string
    headerSubtitle: string
    shortLocation: string
  }
  hero: {
    eyebrow: string
    title: string
    description: string
    ctaLabel: string
    imageUrl: string
  }
  footer: {
    description: string
    legalEmail: string
  }
  cta: {
    eyebrow: string
    title: string
    description: string
  }
  reservation: {
    paymentProviderLabel: string
    instructions: string
  }
  legal: {
    privacy: string
    terms: string
    cookies: string
    security: string
    dataRights: string
  }
  brokerPortal: {
    registrationPolicyTitle: string
    registrationPolicyText: string
    registrationPolicyVersion: string
    registrationConsentLabel: string
  }
  brand: {
    primaryColor: string
    accentColor: string
    logoUrl: string
    logoMarkUrl: string
    logoLightUrl: string
    faviconUrl: string
    appleTouchIconUrl: string
  }
  contact: {
    email: string
    phone: string
    whatsapp: string
    address: string
  }
  social: {
    whatsapp: string
    instagram: string
    linkedin: string
  }
  media: {
    galleryUrls: string[]
  }
  projects: {
    publishedProjectIds: string[]
    presentation: Record<string, {
      coverImageUrl: string
      galleryUrls: string[]
    }>
  }
}

export const DEFAULT_SITE_CONTENT: SiteContent = {
  seo: {
    siteName: activeTenant.tradeName,
    description: '',
    canonicalUrl: '',
    keywords: '',
    robots: 'index,follow',
    ogTitle: '',
    ogDescription: '',
    ogImageUrl: '',
    twitterCard: 'summary_large_image',
  },
  identity: {
    displayName: activeTenant.tradeName,
    legalName: activeTenant.legalName,
    headerSubtitle: '',
    shortLocation: '',
  },
  hero: {
    eyebrow: activeTenant.tradeName,
    title: '',
    description: '',
    ctaLabel: 'Explorar proyectos',
    imageUrl: '',
  },
  footer: {
    description: '',
    legalEmail: activeTenant.email,
  },
  cta: {
    eyebrow: '',
    title: '',
    description: '',
  },
  reservation: {
    paymentProviderLabel: '',
    instructions: '',
  },
  legal: {
    privacy: '',
    terms: '',
    cookies: '',
    security: '',
    dataRights: '',
  },
  brokerPortal: {
    registrationPolicyTitle: '',
    registrationPolicyText: '',
    registrationPolicyVersion: '',
    registrationConsentLabel: '',
  },
  brand: {
    primaryColor: activeTenant.primaryColor,
    accentColor: '#9A7425',
    logoUrl: activeTenant.logoUrl,
    logoMarkUrl: activeTenant.logoUrl,
    logoLightUrl: activeTenant.logoUrl,
    faviconUrl: '',
    appleTouchIconUrl: '',
  },
  contact: {
    email: activeTenant.email,
    phone: '',
    whatsapp: '',
    address: '',
  },
  social: {
    whatsapp: '',
    instagram: '',
    linkedin: '',
  },
  media: {
    galleryUrls: [],
  },
  projects: {
    publishedProjectIds: [],
    presentation: {},
  },
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function projectPresentation(value: unknown): SiteContent['projects']['presentation'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([projectId, raw]) => {
      const item = raw && typeof raw === 'object' && !Array.isArray(raw)
        ? raw as { coverImageUrl?: unknown; galleryUrls?: unknown }
        : {}
      return [projectId, {
        coverImageUrl: typeof item.coverImageUrl === 'string' ? item.coverImageUrl : '',
        galleryUrls: stringArray(item.galleryUrls),
      }]
    }),
  )
}

function normalize(value: unknown): SiteContent {
  const input = value && typeof value === 'object' ? value as Partial<SiteContent> : {}
  return {
    seo: { ...DEFAULT_SITE_CONTENT.seo, ...(input.seo ?? {}) },
    identity: { ...DEFAULT_SITE_CONTENT.identity, ...(input.identity ?? {}) },
    hero: { ...DEFAULT_SITE_CONTENT.hero, ...(input.hero ?? {}) },
    footer: { ...DEFAULT_SITE_CONTENT.footer, ...(input.footer ?? {}) },
    cta: { ...DEFAULT_SITE_CONTENT.cta, ...(input.cta ?? {}) },
    reservation: { ...DEFAULT_SITE_CONTENT.reservation, ...(input.reservation ?? {}) },
    legal: { ...DEFAULT_SITE_CONTENT.legal, ...(input.legal ?? {}) },
    brokerPortal: { ...DEFAULT_SITE_CONTENT.brokerPortal, ...(input.brokerPortal ?? {}) },
    brand: { ...DEFAULT_SITE_CONTENT.brand, ...(input.brand ?? {}) },
    contact: { ...DEFAULT_SITE_CONTENT.contact, ...(input.contact ?? {}) },
    social: { ...DEFAULT_SITE_CONTENT.social, ...(input.social ?? {}) },
    media: { galleryUrls: stringArray(input.media?.galleryUrls) },
    projects: {
      publishedProjectIds: stringArray(input.projects?.publishedProjectIds),
      presentation: projectPresentation(input.projects?.presentation),
    },
  }
}

export async function loadPublicSiteContent(): Promise<SiteContent> {
  if (!isSupabaseConfigured || !supabase) return DEFAULT_SITE_CONTENT

  try {
    const { data, error } = await supabase
      .from('company_settings')
      .select('value')
      .eq('company_id', activeTenant.companyId)
      .eq('key', PUBLIC_KEY)
      .maybeSingle()

    if (error) throw error
    return normalize(data?.value)
  } catch {
    return DEFAULT_SITE_CONTENT
  }
}
